import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';

export const useScheduledEvents = () => {
  const [saving, setSaving] = useState(false);

  const createEvent = useCallback(async (eventData) => {
    setSaving(true);
    try {
      const payload = {
        title_internal: eventData.title?.trim(),
        description_internal: eventData.description?.trim() || null,
        event_type: eventData.eventType || 'meeting',
        status: eventData.status || 'scheduled',
        priority: eventData.priority || 'medium',
        visibility: eventData.visibility || 'internal',
        start_at: eventData.startAt,
        end_at: eventData.endAt || null,
        all_day: !!eventData.allDay,
        owner_colaborador_id: eventData.ownerColaboradorId || null,
        assigned_to_colaborador_id: eventData.assignedToColaboradorId || null,
        business_unit_id: eventData.businessUnitId || null,
        created_by_type: 'human',
        created_by_colaborador_id: eventData.createdByColaboradorId || null,
        source: eventData.source || 'manual',
      };
      if (!payload.title_internal) throw new Error('Título é obrigatório');
      if (!payload.start_at) throw new Error('Data/hora de início é obrigatória');

      const { data, error } = await supabase
        .from('scheduled_events')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;

      if (Array.isArray(eventData.links) && eventData.links.length > 0) {
        const linksRows = eventData.links
          .filter((l) => l.entityType && l.entityId)
          .map((l) => ({
            scheduled_event_id: data.id,
            entity_type: l.entityType,
            entity_id: l.entityId,
            relation_type: l.relationType || 'related',
          }));
        if (linksRows.length > 0) {
          const { error: linkErr } = await supabase
            .from('scheduled_event_links')
            .insert(linksRows);
          if (linkErr) throw linkErr;
        }
      }

      if (Array.isArray(eventData.attendees) && eventData.attendees.length > 0) {
        const attendeesRows = eventData.attendees.map((a) => ({
          scheduled_event_id: data.id,
          attendee_type: a.type || 'internal',
          colaborador_id: a.colaboradorId || null,
          contato_id: a.contatoId || null,
          name: a.name || null,
          email: a.email || null,
          role: a.role || 'attendee',
        }));
        const { error: attErr } = await supabase
          .from('scheduled_event_attendees')
          .insert(attendeesRows);
        if (attErr) throw attErr;
      }

      return { data, error: null };
    } catch (err) {
      console.error('createEvent error:', err);
      return { data: null, error: err.message };
    } finally {
      setSaving(false);
    }
  }, []);

  const updateEvent = useCallback(async (id, updates) => {
    setSaving(true);
    try {
      const dbUpdates = {};
      if (updates.title !== undefined) dbUpdates.title_internal = updates.title?.trim();
      if (updates.description !== undefined) dbUpdates.description_internal = updates.description?.trim() || null;
      if (updates.eventType !== undefined) dbUpdates.event_type = updates.eventType;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.priority !== undefined) dbUpdates.priority = updates.priority;
      if (updates.visibility !== undefined) dbUpdates.visibility = updates.visibility;
      if (updates.startAt !== undefined) dbUpdates.start_at = updates.startAt;
      if (updates.endAt !== undefined) dbUpdates.end_at = updates.endAt || null;
      if (updates.allDay !== undefined) dbUpdates.all_day = !!updates.allDay;
      if (updates.ownerColaboradorId !== undefined) dbUpdates.owner_colaborador_id = updates.ownerColaboradorId || null;
      if (updates.assignedToColaboradorId !== undefined) dbUpdates.assigned_to_colaborador_id = updates.assignedToColaboradorId || null;
      if (updates.businessUnitId !== undefined) dbUpdates.business_unit_id = updates.businessUnitId || null;

      const { data, error } = await supabase
        .from('scheduled_events')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('updateEvent error:', err);
      return { data: null, error: err.message };
    } finally {
      setSaving(false);
    }
  }, []);

  const cancelEvent = useCallback(async (id) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('scheduled_events')
        .update({ status: 'cancelled' })
        .eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('cancelEvent error:', err);
      return { error: err.message };
    } finally {
      setSaving(false);
    }
  }, []);

  const deleteEventPermanently = useCallback(async (id) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('scheduled_events')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('deleteEventPermanently error:', err);
      return { error: err.message };
    } finally {
      setSaving(false);
    }
  }, []);

  const completeEvent = useCallback(async (id) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('scheduled_events')
        .update({ status: 'completed' })
        .eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (err) {
      console.error('completeEvent error:', err);
      return { error: err.message };
    } finally {
      setSaving(false);
    }
  }, []);

  const fetchEventDetails = useCallback(async (id) => {
    try {
      const [eventRes, linksRes, attendeesRes] = await Promise.all([
        supabase.from('scheduled_events').select('*').eq('id', id).single(),
        supabase.from('scheduled_event_links').select('*').eq('scheduled_event_id', id),
        supabase.from('scheduled_event_attendees').select('*').eq('scheduled_event_id', id),
      ]);
      if (eventRes.error) throw eventRes.error;
      return {
        event: eventRes.data,
        links: linksRes.data || [],
        attendees: attendeesRes.data || [],
        error: null,
      };
    } catch (err) {
      console.error('fetchEventDetails error:', err);
      return { event: null, links: [], attendees: [], error: err.message };
    }
  }, []);

  return { createEvent, updateEvent, cancelEvent, completeEvent, deleteEventPermanently, fetchEventDetails, saving };
};

export default useScheduledEvents;
